'use client';

import { GestureResponderEvent, Platform, Pressable } from 'react-native';
import { trackEvent, useDispatch } from '@bonanza-social/toolkit/state';
import { ButtonProps } from './Button.types';
import Box from '../Box/Box';
import Text from '../Text/Text';
import useStyle from '../../hooks/useStyle';
import useHover from '../../hooks/useHover/useHover';
import { memo, useCallback } from 'react';
import {
  LIGHT_VARIANTS,
  DARK_VARIANTS,
  SIZES,
  DISABLED_BUTTON_OPACITY,
  ICON_ONLY_SIZES,
} from './Button.content';
import Icon from '../Icon/Icon';
import Loader from '../Loader/Loader';

function Button({
  variant = 'primary',
  size = 'small',
  light = false,
  text,
  locKey,
  locNS,
  loading,
  disabled,
  leftIcon,
  rightIcon,
  trackingId,
  labelProps,
  children,
  renderLeftIcon,
  renderRightIcon,
  onPress,
  ...rest
}: ButtonProps) {
  const dispatch = useDispatch();
  const style = useStyle(rest);
  const iconOnly = !locKey && Boolean(leftIcon || rightIcon);
  const config = light ? LIGHT_VARIANTS[variant] : DARK_VARIANTS[variant];
  const { isHovered, ...hoverTriggers } = useHover();

  const handlePress = useCallback(
    (e: GestureResponderEvent) => {
      onPress?.(e);

      dispatch(
        trackEvent({
          name: 'button_click',
          params: {
            buttonText: text ?? `${locKey}.${locNS}`,
            buttonSize: size,
            buttonVariant: variant,
            buttonTrackingId: trackingId,
          },
        }),
      );
    },
    [dispatch, text, locKey, locNS, onPress, size, trackingId, variant],
  );

  return (
    <Pressable
      style={style}
      disabled={disabled || loading}
      onPress={handlePress}
      {...(disabled || loading ? undefined : hoverTriggers)}
      accessibilityRole="button"
    >
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        borderRadius="radius-200"
        opacity={disabled ? DISABLED_BUTTON_OPACITY : 1}
        {...SIZES[size]}
        {...(iconOnly && ICON_ONLY_SIZES[size])}
        {...config.box}
        {...(isHovered && config.boxActive)}
      >
        <Box
          flexDirection="row"
          gap="space-200"
          opacity={loading ? 0 : 1}
          centerContent
          flexShrink={1}
        >
          {leftIcon && (
            <Icon
              width="space-400"
              height="space-400"
              name={leftIcon}
              flexShrink={0}
              {...config.icon}
            />
          )}
          {renderLeftIcon?.()}
          {children ??
            ((locKey || !!text) && (
              <Text
                variant={labelProps?.variant ?? (`button-${size}` as any)}
                locKey={locKey}
                locNS={locNS}
                textAlign={labelProps?.textAlign ?? 'center'}
                numberOfLines={
                  labelProps?.numberOfLines ??
                  Platform.select({ web: undefined, default: 1 })
                }
                uppercase={labelProps?.uppercase}
                animatedStyle={labelProps?.animatedStyle}
                wordBreak={labelProps?.wordBreak}
                underline={labelProps?.underline}
                crossedOut={labelProps?.crossedOut}
                color={labelProps?.color as any}
                gradient={labelProps?.gradient as any}
                {...config.text}
              >
                {text}
              </Text>
            ))}
          {rightIcon && <Icon name={rightIcon} {...config.icon} />}
          {renderRightIcon?.()}
        </Box>
        {loading ? <Loader position="absolute" /> : null}
      </Box>
    </Pressable>
  );
}

export default memo(Button);
